import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './RelatoriosIA.css';

const RelatoriosIA = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: 'Olá! Sou o assistente de IA do Portal Escolar SIA. Posso ajudar você com informações sobre alunos, turmas, notas, frequência e calendário escolar.\n\nComo posso ajudar você hoje?',
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedQueries] = useState([
    'Quais alunos estão na turma 9º ano?',
    'Mostre as notas de matemática',
    'Qual a frequência do aluno João?',
    'Quais são as próximas provas?',
    'Liste as turmas disponíveis',
    'Mostre o calendário escolar'
  ]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post('/api/ai/query', {
        query: inputMessage,
        context: messages.slice(-5).map(m => m.content).join('\n')
      });

      const botMessage = {
        id: messages.length + 2,
        type: 'bot',
        content: response.data.response,
        data: response.data.data,
        sql: response.data.sql,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        id: messages.length + 2,
        type: 'bot',
        content: 'Desculpe, ocorreu um erro ao processar sua consulta. Tente novamente.',
        isError: true,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestedQuery = (query) => {
    setInputMessage(query);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderMessageContent = (message) => {
    if (message.data && Array.isArray(message.data) && message.data.length > 0) {
      return (
        <div className="message-with-data">
          <p className="message-text">{message.content}</p>
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  {Object.keys(message.data[0]).map((key) => (
                    <th key={key}>{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {message.data.map((row, index) => (
                  <tr key={index}>
                    {Object.values(row).map((value, i) => (
                      <td key={i}>{value}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {message.sql && (
            <div className="sql-query">
              <span className="sql-label">SQL:</span>
              <code>{message.sql}</code>
            </div>
          )}
        </div>
      );
    }

    return <p className="message-text">{message.content}</p>;
  };

  return (
    <div className="relatorios-ia-container">
      <div className="relatorios-header">
        <div className="header-icon">🎓</div>
        <div className="header-text">
          <h1>Relatórios por IA</h1>
          <p>Assistente inteligente para consultas e relatórios do sistema</p>
        </div>
      </div>

      <div className="chat-container">
        <div className="messages-area">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.type} ${message.isError ? 'error' : ''}`}
            >
              <div className="message-avatar">
                {message.type === 'bot' ? '🎓' : '👤'}
              </div>
              <div className="message-content">
                <div className="message-header">
                  <span className="message-author">
                    {message.type === 'bot' ? 'Assistente IA' : 'Você'}
                  </span>
                  <span className="message-time">{formatTime(message.timestamp)}</span>
                </div>
                {renderMessageContent(message)}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="message bot loading">
              <div className="message-avatar">🎓</div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="suggested-queries">
          <span className="suggested-label">Consultas sugeridas:</span>
          <div className="suggested-chips">
            {suggestedQueries.map((query, index) => (
              <button
                key={index}
                className="query-chip"
                onClick={() => handleSuggestedQuery(query)}
              >
                {query}
              </button>
            ))}
          </div>
        </div>

        <div className="input-area">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite sua consulta aqui... (Ex: Quantos usuários estão cadastrados?)"
            rows={2}
            disabled={isLoading}
          />
          <button
            className="send-button"
            onClick={handleSendMessage}
            disabled={isLoading || !inputMessage.trim()}
          >
            {isLoading ? (
              <span className="spinner"></span>
            ) : (
              <>
                <span>Enviar</span>
                <span className="send-icon">📤</span>
              </>
            )}
          </button>
        </div>

        <div className="disclaimer">
          <span className="disclaimer-icon">📚</span>
          <span>Este assistente pode fornecer informações sobre o sistema escolar. Para dados específicos, consulte os módulos correspondentes.</span>
        </div>
      </div>
    </div>
  );
};

export default RelatoriosIA;
